/**
 * @file Offboarding LIST page (`#/offboarding`) — admin.
 *
 * Queue view of leavers (employees in the `offboarding` phase) with a
 * per-leaver device-resolution progress count. Clicking a row navigates
 * to `/people/:id`, where the actual per-device offboarding workflow
 * lives (see person-detail.js). The old `#/offboarding/:id` detail
 * renderer was removed — main.js redirects that route to /people/:id, so
 * there is exactly one offboarding work surface, not two.
 *
 * Data: a single live `/devices` snapshot grouped by holder in memory
 * (cheap — avoids N full-collection reads), joined with a per-render
 * `/offboardings` fetch for the settled/pending counts.
 */

import { el, clear } from '../utils.js';
import { Icons } from '../icons.js';
import { PHASES } from '../config.js';
import { navigate } from '../router.js';
import { watchEmployees, watchDevices } from '../db.js';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase.js';
import { renderPageHead } from '../page-header.js';
import { SETTLED_DECISION_STATUSES } from '../lib/offboarding-statuses.js';
import { testCollectionName } from '../test-mode.js';
import { sortableTable, listCard } from '../lib/sortable-table.js';
import { loadListState, saveListState, setListContext } from '../lib/list-memory.js';
import { overdueDateCell } from '../lib/overdue.js';
import { renderLoadError } from '../lib/load-error.js';
import { copyable } from '../lib/copyable.js';

// ── List page ──────────────────────────────────────────────────────────────
export function renderOffboardingList(host) {
  clear(host);

  host.appendChild(renderPageHead('Offboarding', 'Leavers, their devices, and the buy/return decision.'));

  // Filter + sort are remembered per browser tab (lib/list-memory.js).
  const state = {
    ...loadListState('offboarding', { filter: 'open', sort: { index: 1, dir: 'asc' } }), // soonest last-day first
    employees: [], offboardings: new Map(), devicesByHolder: new Map(),
  };
  const remember = (patch) => saveListState('offboarding', patch);

  const segActive = el('button', { class: state.filter === 'open' ? 'active' : null }, 'Needs action');
  const segAll    = el('button', { class: state.filter === 'all' ? 'active' : null }, 'All in phase');
  segActive.addEventListener('click', () => { state.filter = 'open'; remember({ filter: 'open' }); segActive.classList.add('active'); segAll.classList.remove('active'); rerender(); });
  segAll.addEventListener('click',    () => { state.filter = 'all';  remember({ filter: 'all' });  segAll.classList.add('active');    segActive.classList.remove('active'); rerender(); });

  const toolbar = el('div', { class: 'toolbar' },
    el('div', { class: 'segment' }, segActive, segAll),
  );
  host.appendChild(toolbar);

  const tableHost = el('div', { class: 'card' });
  host.appendChild(tableHost);
  // Otherwise a watch failure leaves the skeleton up forever (both sources
  // must report before we render, so one failing source hangs the page).
  const showLoadError = () => {
    clear(tableHost);
    tableHost.appendChild(renderLoadError('Could not load leavers.'));
  };

  // Both live sources (employees + /devices) must report at least once
  // before we filter. The "Needs action" filter keys off device counts, so
  // rendering with employees-but-no-devices makes every leaver look like
  // "no devices" and flash into the list for a frame until the /devices
  // snapshot lands and hides the already-resolved ones.
  let employeesLoaded = false;
  let devicesLoaded   = false;

  function rerender() {
    clear(tableHost);
    if (!employeesLoaded || !devicesLoaded) {
      tableHost.appendChild(el('div', { class: 'skeleton', style: 'height:120px' }));
      return;
    }
    const rows = state.employees.map((e) => {
      const off = state.offboardings.get(e.id);
      const decisions = off?.deviceDecisions || {};
      const devices = state.devicesByHolder.get(e.id) || [];
      // Count settled PER HELD DEVICE — a device is resolved only if ITS OWN
      // decision is terminal. The old `Object.values(decisions).filter(...)`
      // counted every settled decision in the doc, including ones for devices
      // the leaver no longer holds (resolved + holder cleared → dropped from
      // allHolderIds). Those stale decisions inflated `settled` and masked
      // genuinely-unresolved held devices — a leaver with an unaccounted-for
      // laptop could compute pending=0 and vanish from "Needs action".
      // Matches the backend's per-device count (sync-bamboo).
      const settled = devices.filter((d) =>
        SETTLED_DECISION_STATUSES.includes(decisions[d.id]?.status),
      ).length;
      const pending = devices.length - settled;
      // Phone tariffs: BambooHR is authoritative. A leaver has an outstanding
      // tariff for as long as an active SIM (no "Date returned") sits on their
      // Bamboo record — regardless of what's been clicked in Gear. The Gear
      // decision is workflow tracking; the real "handled" signal is the admin
      // setting Date returned in Bamboo, which they do only after the line is
      // actually cancelled at the carrier. This is what stops us dropping a
      // leaver while we're still paying for a live SIM.
      const pendingTariffs = (e.assets || [])
        .filter((a) => a.category === 'Sim Card' && !a.dateReturned)
        .length;
      return { ...e, deviceCount: devices.length, settled, pending, pendingTariffs };
    });
    // "Needs action" = any unresolved obligation: a held device without a
    // terminal decision, OR an active tariff without one. (The old
    // `deviceCount === 0` catch-all wrongly surfaced never-had-gear leavers
    // AND fully-resolved-then-cleared ones alike, as "No devices".)
    const visible = state.filter === 'open'
      ? rows.filter((r) => r.pending > 0 || r.pendingTariffs > 0)
      : rows;
    if (!visible.length) {
      tableHost.appendChild(el('div', { class: 'empty' },
        Icons.inbox(),
        el('p', null, state.filter === 'open' ? 'Nothing needs action right now.' : 'No active offboardings.'),
        el('p', { class: 'muted' }, state.filter === 'open'
          ? 'Switch to "All in phase" to see leavers already fully resolved.'
          : 'Leavers appear here automatically once they have a termination date.'),
      ));
      return;
    }
    // Overdue = last day passed AND gear or tariff still unsettled
    // (same "needs action" test the filter uses).
    const lastDayCell = (r) => overdueDateCell(r.endDate, (r.pending ?? 0) > 0 || (r.pendingTariffs ?? 0) > 0, 'left');
    const renderLeaverRow = (r) =>
      el('tr', { onClick: () => navigate(`/people/${r.id}`), 'data-row-link': true },
        el('td', null, r.fullName || '—'),
        el('td', null, lastDayCell(r)),
        el('td', { class: 'muted' }, r.email ? copyable(r.email, { label: 'email' }) : '—'),
        el('td', null, progressCell(r)),
      );
    const renderLeaverCard = (r) => listCard({
      title: r.fullName || '—',
      meta:  [r.email, lastDayCell(r)],
      aside: [progressCell(r)],
      onClick: () => navigate(`/people/${r.id}`),
    });
    tableHost.appendChild(sortableTable({
      columns: [
        { label: 'Employee', sortValue: (r) => r.fullName },
        { label: 'Last day', sortValue: (r) => r.endDate },
        { label: 'Email',    sortValue: (r) => r.email },
        // Outstanding work (unresolved devices + tariffs); fully-resolved sort first.
        { label: 'Devices',  sortValue: (r) => r.pending + r.pendingTariffs },
      ],
      rows: visible,
      renderRow: renderLeaverRow,
      renderCard: renderLeaverCard,
      initialSort: state.sort,
      onOrder: ({ rows: ordered, sortIndex, sortDir }) => {
        state.sort = { index: sortIndex, dir: sortDir };
        remember({ sort: state.sort });
        setListContext({ source: 'offboarding', ids: ordered.map((r) => r.id), href: '#/offboarding', label: 'Offboarding' });
      },
    }));
  }

  // Two live subscriptions. /devices is read ONCE (initial snapshot + deltas
  // only) and grouped by holder in memory — previously this page did
  // getDocs(/devices) N times per page open, one per leaver, reading the
  // full ~1000-row collection each time. /offboardings is small (one doc
  // per leaver) so one getDocs per /employees change is fine.
  state.allDevices = [];

  function rebuildDevicesByHolder() {
    state.devicesByHolder = new Map();
    for (const d of state.allDevices) {
      if (d.personal) continue; // the leaver's own laptop — nothing to settle
      const ids = Array.isArray(d.allHolderIds) && d.allHolderIds.length
        ? d.allHolderIds
        : (d.currentHolder ? [d.currentHolder] : []);
      for (const id of ids) {
        const list = state.devicesByHolder.get(id) || [];
        list.push(d);
        state.devicesByHolder.set(id, list);
      }
    }
  }

  const unsubEmployees = watchEmployees(PHASES.offboarding, async (employees) => {
    state.employees = employees;
    // /offboardings is small; one fetch per employee-snapshot is fine.
    const offSnap = await getDocs(collection(db, testCollectionName('offboardings')));
    state.offboardings = new Map(offSnap.docs.map((d) => [d.id, d.data()]));
    employeesLoaded = true;
    rerender();
  }, showLoadError);
  const unsubDevices = watchDevices({}, (devices) => {
    state.allDevices = devices;
    devicesLoaded = true;
    rebuildDevicesByHolder();
    rerender();
  }, showLoadError);
  host.addEventListener('cleanup', () => { unsubEmployees(); unsubDevices(); }, { once: true });
}

// Compact "what's outstanding" cell for a leaver row: device-resolution
// progress plus a phone-tariff note. Keeps the familiar "X/Y resolved"
// wording for devices; the "All resolved" badge shows only when there's
// genuinely nothing left (devices AND tariffs).
function progressCell(r) {
  const parts = [];
  if (r.deviceCount > 0) {
    if (r.pending > 0) {
      parts.push(el('span', null, `${r.settled}/${r.deviceCount} resolved`));
    } else if (r.pendingTariffs === 0) {
      parts.push(el('span', { class: 'badge badge-done' }, 'All resolved'));
    } else {
      parts.push(el('span', { class: 'muted' }, 'Devices resolved'));
    }
  } else if (r.pendingTariffs === 0) {
    parts.push(el('span', { class: 'muted' }, 'No devices'));
  }
  if (r.pendingTariffs > 0) {
    parts.push(el('span', null, `Tariff pending${r.pendingTariffs > 1 ? ` (${r.pendingTariffs})` : ''}`));
  }
  if (parts.length <= 1) return parts[0] || el('span', { class: 'muted' }, '—');
  const out = [];
  parts.forEach((p, i) => { if (i) out.push(el('span', { class: 'muted' }, ' · ')); out.push(p); });
  return el('span', null, ...out);
}
